'use client';
import { useContext, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { getCookie, setCookie } from 'cookies-next';
import { ChevronRight } from 'lucide-react';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import ContentStackImage from '@/components/customercenter-ui/common/contentStackImage/contentStackImage';
import { convertRetriedBrandSites, isRetiredBrand } from '@/components/customercenter-ui/utils/assetSaleUtils';
import { CreditSummaryContext } from '@/context/creditSummaryProvider';
import { useAccount } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { GlobalPageTexts } from '@/helpers/i18n';
import { setNewRelicPageAttributes } from '@/helpers/utils/newRelicUtils';
import {
  DisableDropDown,
  PathNames,
  extractCustomerId,
  getSelectedDivision,
  TranslateText,
  createUrl,
  getBreadCrumb,
} from '@/helpers/utils/siteUtils';
import {
  BREADCRUMB,
  COOKIE_KEYS,
  DEFAULT_SITE_LANG,
  DISABLE_ACCOUNT_NUMBER,
} from '../../../../../../constants/constant';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/customSelect/customSelect';
import Loader from '../../../ui/loader';
import { PAYLOAD_QUERY } from '../../constants';
import { DisablePages } from '../../types';

const CustomerDetailHeader = ({ disablePages }: { disablePages: DisablePages[] }) => {
  const Texts = TranslateText(GlobalPageTexts, DEFAULT_SITE_LANG);
  const [laoding, setLoading] = useState(false);
  const params = useSearchParams();
  const projectSettings: ProjectSettings = useProjSettings();
  const { setSelectedSiteInSession } = useAccount();
  const router = useRouter();
  const pathName = usePathname();
  const [customerId, setCustomerId] = useState('');
  const { customerData } = useContext(CreditSummaryContext);
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [disable, setDisable] = useState(false);
  const [siteList, setSiteList] = useState<any[]>([]);
  const [breadCrumb, setBreadCrumb] = useState('');
  const [showAccountNumber, setShowAccountNumber] = useState(true);
  const [isOrderDetailsPage, setIsOrderDetailsPage] = useState(false);
  const currentPath = pathName?.split('/')?.[1];

  useEffect(() => {
    const divisionCode =
      decodeURI(params.get(PAYLOAD_QUERY.DIVISION_CODE) ?? '')?.trim() || getSelectedDivision() || 'ZB';
    const cookieSite = divisionCode && `${divisionCode}Site`;
    setCookie('selectedSite', cookieSite ?? selectedSite, { sameSite: 'none', secure: true, partitioned: true });
    setCookie(COOKIE_KEYS?.CURRENT_ENVIRONMENT, projectSettings?.environment, {
      sameSite: 'none',
      secure: true,
      partitioned: true,
    });
    setSelectedSite(cookieSite);
  }, []);

  useEffect(() => {
    if (selectedSite) {
      setSelectedSiteInSession({
        selectedSite,
        divisionCode: selectedSite?.slice(0, 2),
        isRetiredBrand: isRetiredBrand(projectSettings, selectedSite),
      });
    }
  }, [selectedSite]);

  useEffect(() => {
    if (projectSettings?.activeBrands) {
      const activeBrands = JSON.parse(projectSettings?.activeBrands as string);
      const retiredBrands = JSON.parse(projectSettings?.retiredBrands as string);

      const siteList = projectSettings?.retiredBrandsEnabled
        ? convertRetriedBrandSites(activeBrands, retiredBrands, true)
        : convertRetriedBrandSites(activeBrands, undefined, false);

      setSiteList(siteList);
    }
  }, [projectSettings]);

  useEffect(() => {
    const customerData = getCookie(COOKIE_KEYS.URL_HISTORY);
    if (DisableDropDown(disablePages, currentPath)) {
      setDisable(true);
    }
    if (BREADCRUMB.includes(currentPath)) {
      let breadCrumb = getBreadCrumb(currentPath);
      if (customerData && !breadCrumb) {
        const searchPage = JSON.parse(customerData);
        breadCrumb = searchPage[PathNames.SEARCH]?.name ?? '';
      }
      setBreadCrumb(breadCrumb);
    }

    if (customerData) {
      const CustomerAccountNumber = JSON.parse(customerData);
      const CustomerLink = CustomerAccountNumber[PathNames.CUSTOMER_DETAILS];
      if (CustomerLink?.link || params.get(PAYLOAD_QUERY.CUSTOMER_ID)) {
        const customerId = extractCustomerId(CustomerLink?.link) ?? params.get(PAYLOAD_QUERY.CUSTOMER_ID) ?? '';
        setCustomerId(customerId);
      } else {
        setCustomerId('');
      }
    }

    if (DISABLE_ACCOUNT_NUMBER?.includes(currentPath)) {
      setShowAccountNumber(false);
    } else {
      setShowAccountNumber(true);
    }
  }, [currentPath]);

  useEffect(() => {
    if (currentPath != 'thank-you') {
      if (!customerId || customerId !== customerData?.customerId) {
        setCustomerId(customerData?.customerId);
      }
    }
  }, [customerData]);

  useEffect(() => {
    if (currentPath === 'order-details') {
      setIsOrderDetailsPage(true);
      setDisable(true);
    } else {
      setIsOrderDetailsPage(false);
    }
  }, [currentPath]);

  return (
    <section className="flex w-full items-center border-b-[1px] border-[#F0F0F0] px-6 py-2">
      {laoding && <Loader />}
      <section className="my-1 mr-auto flex items-center text-lg font-bold">
        {customerId &&
          showAccountNumber &&
          (!isOrderDetailsPage ? (
            <p>
              {Texts.accountNumber} <span className="gbmask">{customerId}</span>
            </p>
          ) : (
            <>
              {Texts.accountNumber}
              <div
                onClick={() => {
                  const selectedDivision = getSelectedDivision() ?? 'ZB';
                  const customerDetailsUrl = `/customer-details/${createUrl(customerId, customerId, selectedDivision)}`;
                  router.push(customerDetailsUrl ?? '');
                  setLoading(true);
                }}
                className="gbmask ml-1 cursor-pointer text-blueLight underline hover:no-underline"
              >
                {customerId}
              </div>
            </>
          ))}
        {breadCrumb && customerId ? (
          <>
            {' '}
            <ChevronRight /> {breadCrumb}
          </>
        ) : (
          <>{breadCrumb}</>
        )}
      </section>
      <section>
        <Select
          disabled={disable}
          defaultValue={selectedSite}
          onValueChange={(value) => {
            setNewRelicPageAttributes({ division: value?.slice(0, 2) });
            setSelectedSite(value);
            setCookie('selectedSite', value, { sameSite: 'none', secure: true, partitioned: true });
            setCookie(COOKIE_KEYS?.CURRENT_ENVIRONMENT, projectSettings?.environment, {
              sameSite: 'none',
              secure: true,
              partitioned: true,
            });
            const currentPageBaseUrl = window?.location?.pathname;
            if (currentPageBaseUrl) {
              window.location.href = currentPageBaseUrl;
            }
          }}
          value={selectedSite}
        >
          <SelectTrigger
            className={
              'w-72 overflow-hidden rounded-md border-[1px] border-foggyGray py-6 ' + (disable ? 'bg-[#E6E6E6]' : '')
            }
          >
            <SelectValue />
          </SelectTrigger>

          <SelectContent className="max-h-[400px] overflow-y-auto bg-white">
            <SelectGroup>
              {siteList?.map((items, index: number) => (
                <SelectItem
                  value={items?.name}
                  key={`${index}-${items?.name}`}
                  className={`flex h-[50px] items-center justify-center overflow-hidden ${
                    index !== siteList.length - 1 ? 'border-b-[1px] border-[#D9D9D9]' : ''
                  }`}
                >
                  <ContentStackImage
                    src={items?.logo}
                    alt={items?.name}
                    imageWidth={160}
                    imageHeight={30}
                    className="h-[28px] object-contain"
                  />
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </section>
    </section>
  );
};

export default CustomerDetailHeader;
